import { Fragment, useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../../components/Reveal';
import { prefersReducedMotion } from './data';

/**
 * Small building blocks shared by the home sections: gold-accent markup in
 * copy, the masked word-by-word heading reveal, the numbered section label
 * and the section head.
 */

/** Plain text with `*words*` lit gold — or wrapped in `Tag` (the hero uses <em>). */
export function rich(text: string, Tag: ElementType = 'span'): ReactNode {
  const parts = text.split(/\*([^*]+)\*/);
  if (parts.length === 1) return text;
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <Tag key={i} className={Tag === 'span' ? 'accent' : undefined}>
        {p}
      </Tag>
    ) : (
      <Fragment key={i}>{p}</Fragment>
    ),
  );
}

/** `rich` with the marks removed, for accessible names. */
export const plain = (text: string) => text.replace(/\*/g, '');

/**
 * A heading whose words rise out of a mask when it scrolls into view (the
 * reference's [data-split]). Without IntersectionObserver, or with reduced
 * motion, it is an ordinary heading. Screen readers get the sentence whole.
 */
export function Split({
  text,
  as: Tag = 'h2',
  id,
  className,
}: {
  text: string;
  as?: ElementType;
  id?: string;
  className?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const [armed] = useState(() => typeof IntersectionObserver !== 'undefined' && !prefersReducedMotion());
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!armed || !el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [armed]);

  if (!armed) {
    return (
      <Tag id={id} className={className}>
        {rich(text)}
      </Tag>
    );
  }

  let w = 0;
  const words = (s: string) =>
    s.split(/(\s+)/).map((p, i) => {
      if (!p) return null;
      if (/^\s+$/.test(p)) return <Fragment key={i}>{p}</Fragment>;
      const n = w++;
      return (
        <span className="sw" key={i}>
          <span style={{ '--w': n } as React.CSSProperties}>{p}</span>
        </span>
      );
    });
  const parts = text.split(/\*([^*]+)\*/);

  return (
    <Tag
      ref={ref}
      id={id}
      className={className}
      aria-label={plain(text)}
      data-split-ready=""
      data-in={inView ? '' : undefined}
    >
      <span aria-hidden="true">
        {parts.map((p, i) =>
          i % 2 === 1 ? (
            <span className="accent" key={i}>
              {words(p)}
            </span>
          ) : (
            <Fragment key={i}>{words(p)}</Fragment>
          ),
        )}
      </span>
    </Tag>
  );
}

/** "02 / Featured venues ··· Photos, capacity and prices up front" */
export function SecLabel({ n, label, note }: { n: number; label: string; note?: string }) {
  return (
    <div className="wrap">
      <Reveal className="sec-label">
        <span>
          <b>{String(n).padStart(2, '0')}</b>
          <i>/</i>
          {label}
        </span>
        {note && <span>{note}</span>}
      </Reveal>
    </div>
  );
}

/** The design's .sec-head: split title, one helpful line, an arrow link. */
export function Head({
  id,
  title,
  sub,
  action,
}: {
  id: string;
  title: string;
  sub?: string;
  action?: { to: string; label: string };
}) {
  return (
    <Reveal className="sec-head">
      <div>
        <Split text={title} id={id} />
        {sub && <p>{sub}</p>}
      </div>
      {action && <ArrowLink to={action.to} label={action.label} />}
    </Reveal>
  );
}

export function ArrowLink({ to, label, as = 'link' }: { to: string; label: string; as?: 'link' | 'span' }) {
  const body = (
    <>
      <span>{label}</span>
      <i className="ri-arrow-right-line i" aria-hidden="true" />
    </>
  );
  if (as === 'span') return <span className="link-arrow">{body}</span>;
  return (
    <Link className="link-arrow" to={to}>
      {body}
    </Link>
  );
}
